import { Money } from "@redotech/money";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { useMemo } from "react";

export function useOnlineStoreSessions(
  dateFilter: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
): number | null | undefined {
  const conversionRpcClient = useRequiredContext(
    RedoConversionRpcClientContext,
  );

  const dateRange = useMemo(() => {
    if (!dateFilter) {
      return undefined;
    }
    return {
      start: Temporal.Instant.from(`${dateFilter[0].toString()}T00:00:00Z`),
      end: Temporal.Instant.from(`${dateFilter[1].toString()}T23:59:59.999Z`),
    };
  }, [dateFilter]);

  const sessionsLoad = useLoad(
    async (signal) => {
      if (!dateRange) {
        return null;
      }
      const result = await conversionRpcClient.getOnlineStoreSessions(
        { dateRange },
        { signal },
      );
      return result.sessions;
    },
    [conversionRpcClient, dateRange],
  );

  return dateRange ? sessionsLoad.value : null;
}

export interface RescuePanelAttribution {
  revenueFromPanel: Money;
  numImpressions: number;
  numEngagements: number;
  numOrders: number;
  conversionRate: number;
  conversionRateChangeComparedToPreviousPeriod: number | null;
}

export function useRescuePanelAttribution(
  dateFilter?: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
): RescuePanelAttribution | null {
  const conversionRpcClient = useRequiredContext(
    RedoConversionRpcClientContext,
  );

  const dateRange = useMemo(() => {
    if (!dateFilter) {
      return undefined;
    }
    return {
      start: Temporal.Instant.from(`${dateFilter[0].toString()}T00:00:00Z`),
      end: Temporal.Instant.from(`${dateFilter[1].toString()}T23:59:59.999Z`),
    };
  }, [dateFilter]);

  const attributionLoad = useLoad(
    async (signal) => {
      return await conversionRpcClient.getRescuePanelAttribution(
        { dateRange },
        { signal },
      );
    },
    [conversionRpcClient, dateRange],
  );

  return attributionLoad.value ?? null;
}
